import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Pagination,
  SearchInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { EnvelopeIcon, ExclamationCircleIcon, PlusCircleIcon, SearchIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { useApiFetch } from '../../../api';
import { useMailDomains } from '../../../hooks/useMailDomains';
import { formatDateNumeric } from '../../../utils/dateFormat';
import type { MailDomain } from '../../../types/models';
import { AntispamStatusLabel } from './AntispamStatusLabel';
import MailDomainCreateModal from './MailDomainCreateModal';

function Emails() {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const { mailDomains, loading, error, refetch } = useMailDomains();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MailDomain | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  const filteredDomains = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return mailDomains;
    return mailDomains.filter((md) => md.organization_domain_name.toLowerCase().includes(q));
  }, [mailDomains, debouncedSearch]);

  const paginatedDomains = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredDomains.slice(start, start + perPage);
  }, [filteredDomains, page, perPage]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setDeleteError(null);

    try {
      const response = await apiFetch(`/emails/domains/${deleteTarget.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error((body as { message?: string })?.message ?? `HTTP error! status: ${response.status}`);
      }

      setDeleteTarget(null);
      refetch();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete mail domain');
    } finally {
      setIsDeleting(false);
    }
  };

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const renderContent = () => {
    if (loading) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load mail domains"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (mailDomains.length === 0) {
      return (
        <EmptyState titleText="No mail domains yet" icon={EnvelopeIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Create your first mail domain to start managing mailboxes for your organization.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => setIsCreateOpen(true)}>
                Create mail domain
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <SearchInput
                placeholder="Search by domain..."
                value={searchValue}
                onChange={(_event, value) => setSearchValue(value)}
                onClear={() => setSearchValue('')}
              />
            </ToolbarItem>
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={filteredDomains.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
              />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>

        {filteredDomains.length === 0 ? (
          <EmptyState titleText="No mail domains match your search" icon={SearchIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>Try a different search term, or clear the filter to see all mail domains.</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={() => setSearchValue('')}>
                  Clear filter
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          <Table aria-label="Mail domains list" variant="compact">
            <Thead>
              <Tr>
                <Th>Domain</Th>
                <Th>IMAP server</Th>
                <Th>Mode</Th>
                <Th>Antispam</Th>
                <Th>Invoicing</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedDomains.map((md) => (
                <Tr key={md.id}>
                  <Td dataLabel="Domain">
                    <Link to={`/datacenter/emails/${md.id}`}>{md.organization_domain_name}</Link>
                  </Td>
                  <Td dataLabel="IMAP server">
                    {md.imap_server_host !== null ? (
                      md.imap_server_host
                    ) : (
                      <Content component="small" className="pf-v6-u-color-200">
                        — stale binding
                      </Content>
                    )}
                  </Td>
                  <Td dataLabel="Mode">
                    {md.imap_server_mode === 'shared' && <Label color="blue">Shared</Label>}
                    {md.imap_server_mode === 'dedicated' && <Label color="green">Dedicated</Label>}
                    {md.imap_server_mode === null && '—'}
                  </Td>
                  <Td dataLabel="Antispam">
                    <AntispamStatusLabel platformId={md.antispam_platform_id} platforms={[]} />
                  </Td>
                  <Td dataLabel="Invoicing">{formatDateNumeric(md.next_billing_at)}</Td>
                  <Td dataLabel="Actions">
                    <Button
                      variant="link"
                      component={(props) => <Link {...props} to={`/datacenter/emails/${md.id}`} />}
                    >
                      View
                    </Button>
                    <Button variant="link" isDanger onClick={() => setDeleteTarget(md)}>
                      Delete
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}

        {filteredDomains.length > 0 && (
          <Pagination
            itemCount={filteredDomains.length}
            perPage={perPage}
            page={page}
            onSetPage={onSetPage}
            onPerPageSelect={onPerPageSelect}
            variant="bottom"
          />
        )}
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem isActive>Emails</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <EnvelopeIcon />
              </Icon>
              Emails
            </Content>
            <Content component="p">Manage mail domains, mailboxes, and aliases</Content>
          </FlexItem>
          {!loading && (
            <FlexItem>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                Create mail domain
              </Button>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>

      <Modal
        variant="small"
        isOpen={deleteTarget !== null}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteError(null);
        }}
        aria-label="Delete mail domain"
      >
        <ModalHeader title="Delete mail domain" />
        <ModalBody>
          {deleteError && (
            <Alert variant="danger" isInline title="Delete failed" className="pf-v6-u-mb-md">
              {deleteError}
            </Alert>
          )}
          <Content>
            Delete mail configuration for <strong>{deleteTarget?.organization_domain_name}</strong>? This removes the
            binding between the domain and its IMAP server. Mailboxes and aliases on the IMAP server are NOT removed.
          </Content>
        </ModalBody>
        <ModalFooter>
          <Button variant="danger" onClick={handleDelete} isLoading={isDeleting} isDisabled={isDeleting}>
            Delete
          </Button>
          <Button
            variant="link"
            onClick={() => {
              setDeleteTarget(null);
              setDeleteError(null);
            }}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <MailDomainCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        mailDomains={mailDomains}
        onCreated={refetch}
      />
    </>
  );
}

export { Emails };
